import React, { useEffect, useState } from 'react';
import { Button, FormControl, TextField, Paper, Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import RouterLink from 'redux-first-router-link';
import { Logo } from '../../common/Logo';
import { PasswordTextField } from '../../common/PasswordTextField';
import { loginRequest } from '../../../store/actions/user';
import { REQUEST_TYPES } from '../../../api/requests/requestTypes';
import { getRequestError, isRequestSuccessfullyLoaded } from '../../../store/selectors/requests';
import { useLocalizationContext } from '../../../localization/LocalizationContext';
import { isUserAuthorized } from '../../../store/selectors/user';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';

import { Theme } from '../../../themes/types';

const useStyles = makeStyles<Theme>(theme => ({
    main: {
        width: 376,
        display: 'block',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
    },
    paper: {
        marginTop: 24,
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        background: theme.palette.contentPaperBackground,
        color: theme.palette.contentPaperText,
        border: `1px solid ${theme.palette.contentPaperBorder}`,
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)'
    },
    field: {
        '& .MuiInputBase-root': {
            color: theme.palette.dateRangeText
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.inputLabelColor
        },
        '& .MuiFormLabel-root.Mui-focused': {
            color: theme.palette.dateRangeLabelHighlighted
        },
        '& .MuiSvgIcon-root': {
            color: theme.palette.downArrowColor
        }
    },
    submit: {
        width: 148,
        marginTop: 24,
        '&.MuiButton-containedPrimary': {
            backgroundColor: theme.palette.modalFooterPrimaryButtonBg,
            color: theme.palette.modalFooterPrimaryButtonText
        },
        '&.MuiButton-containedPrimary:hover': {
            backgroundColor: theme.palette.modalFooterPrimaryButtonHoverBg
        },
        '&.MuiButton-containedPrimary.Mui-disabled': {
            backgroundColor: theme.palette.modalFooterPrimaryButtonDisabledBg,
            color: theme.palette.modalFooterPrimaryButtonDisabledText
        }
    },
    link: {
        color: theme.palette.contentLinkColor,
        textDecorationColor: theme.palette.contentLinkColor
    }
}));

export const Login = () => {
    const dispatch = useAppDispatch();
    const localization = useLocalizationContext();
    const error = useAppSelector(state => getRequestError(state, REQUEST_TYPES.LOGIN, undefined));
    const isRequestLoaded = useAppSelector(state => isRequestSuccessfullyLoaded(state, REQUEST_TYPES.LOGIN, undefined));
    const authState = useAppSelector(isUserAuthorized);
    const classes = useStyles();
    const theme = useTheme<Theme>();
    const [email, setEmail] = useState('');
    const [errorCode, setErrorCode] = useState<string | boolean | null>(null);
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const canSubmit = email.length > 0 && password.length > 0;

    const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setErrorCode(false);
        setEmail(event.target.value);
    };
    const onPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setErrorCode(false);
        setPassword(event.target.value);
    };
    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        dispatch(loginRequest(email, password));
        setIsLoading(true);
    };

    useEffect(() => {
        setErrorCode((error && error?.data?.error) || null);
    }, [error]);

    useEffect(() => {
        if (error) {
            setIsLoading(false);
        }
    }, [error]);

    useEffect(() => {
        if (isRequestLoaded && !authState) {
            setErrorCode(true);
            setIsLoading(false);
        }
    }, [authState, isRequestLoaded]);

    return (
        <Box className={classes.main}>
            <Box display="flex" justifyContent="center">
                <Logo color={theme.palette.headerLogoColor} />
            </Box>
            <Paper className={classes.paper}>
                <Typography component="h1" variant="h5">
                    {localization.getText('login.title')}
                </Typography>
                <form onSubmit={onSubmit}>
                    {errorCode && (
                        <Box mb="6px" mt="12px">
                            <Typography color="error" data-test="login-error">
                                {errorCode === 'ERR_PASSWORD_EXPIRED'
                                    ? localization.getText('login.auth_error.expired')
                                    : localization.getText('login.auth_error.general')}
                            </Typography>
                        </Box>
                    )}
                    <FormControl margin="normal" required fullWidth>
                        <TextField
                            className={classes.field}
                            variant="outlined"
                            label={localization.getText('login.email.label')}
                            name="login-email"
                            error={!!errorCode}
                            autoFocus
                            value={email}
                            onChange={onEmailChange}
                            inputProps={{ 'data-test': 'login-email' }}
                            autoComplete="username"
                        />
                    </FormControl>
                    <FormControl margin="normal" required fullWidth>
                        <PasswordTextField
                            className={classes.field}
                            name="login-password"
                            error={!!errorCode}
                            value={password}
                            label={localization.getText('login.password.label')}
                            onChange={onPasswordChange}
                            inputProps={{ 'data-test': 'login-password' }}
                            autoComplete="current-password"
                        />
                    </FormControl>
                    <Box display="flex" justifyContent="space-between" alignItems="baseline">
                        <RouterLink className={classes.link} to="/recovery">
                            {localization.getText('login.forgotPassword')}
                        </RouterLink>
                        <Button
                            name="login-submit"
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={!canSubmit || isLoading}
                            className={classes.submit}
                            data-test="login-submit"
                        >
                            {isLoading
                                ? localization.getText('login.submit.in_progress')
                                : localization.getText('login.submit.title')}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};
